import {
  ArrowBackIosNew,
  ArrowForwardIos,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import { AlbumImage } from "model/albumModel";
import FileIcon from "./FileIcon";

interface AlbumGalleryProps {
  items: AlbumImage[];
  /** URL della miniatura per un dato upload (blob nel dialog, pubblico nello share). */
  thumbFor: (idupload: number) => string | undefined;
  /** Testo mostrato quando non c'è didascalia. */
  labelFor?: (idupload: number) => string;
}

/**
 * Galleria di un album: griglia di miniature, click → lightbox con
 * avanti/indietro, didascalia e tasto full screen (dialog a tutto schermo).
 * SOLO LETTURA: l'editing (selezione, ordine, didascalie) sta in AlbumEditDialog.
 */
const AlbumGallery: React.FC<AlbumGalleryProps> = ({ items, thumbFor, labelFor }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [fullScreen, setFullScreen] = useState(false);
  const current = openIndex !== null ? items[openIndex] : null;

  if (!items || items.length === 0) return null;

  const go = (delta: number) => {
    if (openIndex === null) return;
    setOpenIndex((openIndex + delta + items.length) % items.length);
  };

  const close = () => {
    setOpenIndex(null);
    setFullScreen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
          gap: 1,
        }}
      >
        {items.map((im, index) => {
          const thumb = thumbFor(im.idupload);
          const alt = im.caption || labelFor?.(im.idupload) || "";
          return (
            <Box
              key={`${im.idupload}-${index}`}
              onClick={() => setOpenIndex(index)}
              sx={{
                position: "relative",
                aspectRatio: "1 / 1",
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {thumb ? (
                <img
                  src={thumb}
                  alt={alt}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <FileIcon filename={labelFor?.(im.idupload) || ""} />
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog open={current !== null} onClose={close} maxWidth="lg" fullScreen={fullScreen}>
        {current && (
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              p: 1,
              ...(fullScreen
                ? { width: "100vw", height: "100vh", bgcolor: "background.paper" }
                : {}),
            }}
          >
            <IconButton
              onClick={() => setFullScreen((v) => !v)}
              aria-label="full screen"
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
            >
              {fullScreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            >
              <IconButton onClick={() => go(-1)} aria-label="precedente">
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
              {thumbFor(current.idupload) ? (
                <img
                  src={thumbFor(current.idupload)}
                  alt={current.caption || ""}
                  style={{
                    maxHeight: fullScreen ? "86vh" : "70vh",
                    maxWidth: fullScreen ? "86vw" : "80vw",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <FileIcon filename={labelFor?.(current.idupload) || ""} />
              )}
              <IconButton onClick={() => go(1)} aria-label="successiva">
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {current.caption || labelFor?.(current.idupload) || ""}
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default AlbumGallery;
